import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Bell, Loader2 } from "lucide-react";
import { useRateLimit, formatTimeRemaining } from "@/hooks/use-rate-limit";
import { AltchaWidget } from "@/components/AltchaWidget";

const NewsletterSubscribe = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [altchaToken, setAltchaToken] = useState<string | null>(null);
  const { toast } = useToast();
  
  // Rate limit: 5 attempts per hour (client-side UX guard; server enforces too)
  const { isLimited, remainingAttempts, timeUntilReset, checkLimit, recordAttempt } = useRateLimit({
    key: "newsletter_subscribe",
    maxAttempts: 5,
    windowMs: 60 * 60 * 1000,
  });

  // Force re-render every minute when limited to update countdown
  const [, setTick] = useState(0);
  useEffect(() => {
    if (isLimited && timeUntilReset > 0) {
      const interval = setInterval(() => setTick(t => t + 1), 60000);
      return () => clearInterval(interval);
    }
  }, [isLimited, timeUntilReset]);

  const handleAltchaToken = useCallback((token: string | null) => {
    setAltchaToken(token);
  }, []);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "Email required",
        description: "Please enter your email address.",
        variant: "destructive",
      });
      return;
    }

    if (!altchaToken) {
      toast({
        title: "Verification required",
        description: "Please complete the security verification.",
        variant: "destructive",
      });
      return;
    }

    // Check rate limit before making request
    if (!checkLimit()) {
      toast({
        title: "Too many attempts",
        description: `Please try again in ${formatTimeRemaining(timeUntilReset)}.`,
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    recordAttempt();

    try {
      const { data, error } = await supabase.functions.invoke("newsletter-subscribe", {
        body: { email, altchaToken },
      });

      if (error) {
        throw error;
      }

      if (data?.success) {
        toast({
          title: "Request received",
          description: data.message || "If this email is not already subscribed, you'll receive our updates.",
        });
        setEmail("");
      } else {
        throw new Error(data?.error || "Subscription failed");
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Unknown error";
      console.error("Subscription error:", message);
      toast({
        title: "Error",
        description: "Failed to subscribe. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="cyber-card border-primary/30 rounded-xl p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-accent/20 border border-accent/30 rounded-lg flex items-center justify-center">
          <Bell className="h-5 w-5 text-accent" />
        </div>
        <div>
          <h3 className="font-heading font-semibold text-foreground">Threat Intelligence Alerts</h3>
          <p className="text-xs font-mono text-muted-foreground">// SUBSCRIBE</p>
        </div>
      </div>
      <p className="text-muted-foreground text-sm mb-4">
        Get continuous notifications on new vulnerabilities, threat disclosures, and security updates.
      </p>
      <form onSubmit={handleSubscribe} className="space-y-3">
        <div className="flex gap-2">
          <Input
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 bg-secondary/50 border-border focus:border-primary"
            disabled={isLoading || isLimited}
          />
          <Button 
            type="submit" 
            disabled={isLoading || isLimited || !altchaToken}
            className="bg-accent hover:bg-accent/90 text-accent-foreground font-medium"
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Subscribe"}
          </Button>
        </div>
        <AltchaWidget onToken={handleAltchaToken} className="mt-2" />
      </form>
      <div className="text-xs text-muted-foreground mt-3 flex items-center gap-2">
        <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
        {isLimited 
          ? `Rate limited. Try again in ${formatTimeRemaining(timeUntilReset)}.`
          : "Unsubscribe anytime. No spam."
        }
      </div>
    </div>
  );
};

export default NewsletterSubscribe;
